/**
 * One icon.
 *
 * The component knows nothing about which icon set is installed. It reads
 * `ICON_PACK` from `icon-pack.ts`, the single file that chooses one, and
 * draws the artwork it finds there.
 *
 * ## Decorative or meaningful
 *
 * Decorative is the default, because in ArcGRC it nearly always is: the
 * meaning sits in the text beside the mark, and `docs/SPACE_DETAILS.md`
 * requires that a scene failing to draw loses nothing. A decorative icon is
 * `aria-hidden` and unfocusable.
 *
 * An icon that carries meaning on its own takes a `title`, which becomes its
 * accessible name. Prefer text; reach for `title` only when the mark really
 * is the only thing there.
 *
 * ## Size
 *
 * `size` is a CSS length for both edges, so the box never changes with the
 * artwork's own aspect ratio and a late-loading icon cannot move the layout.
 * The default 1em follows the surrounding text.
 */

import { ICON_PACK } from "./icon-pack.ts";
import type { IconName } from "./icon-names.ts";

export type { IconName } from "./icon-names.ts";
export { ICON_NAMES, SPACE_ICON_NAMES, OBJECT_ICON_NAMES } from "./icon-names.ts";

export function Icon({
  name,
  size = "1em",
  className,
  title,
}: {
  readonly name: IconName;
  readonly size?: string;
  readonly className?: string;
  /** Give this only when the icon is the sole carrier of its meaning. */
  readonly title?: string;
}) {
  const icon = ICON_PACK[name];
  const decorative = title === undefined;
  const paths = typeof icon.path === "string" ? [icon.path] : icon.path;

  return (
    <svg
      viewBox={`0 0 ${icon.width} ${icon.height}`}
      width={size}
      height={size}
      className={className}
      // The box is square and reserved, so the artwork never shifts the row.
      style={{ display: "inline-block", flexShrink: 0, overflow: "visible" }}
      fill="currentColor"
      aria-hidden={decorative ? true : undefined}
      focusable="false"
      role={decorative ? "presentation" : "img"}
      {...(decorative ? {} : { "aria-label": title })}
    >
      {title !== undefined && <title>{title}</title>}
      {paths.map((path, index) => (
        <path
          key={index}
          d={path}
          // A two-layer style draws its lower layer faintly. A one-layer
          // style never reaches this branch.
          opacity={paths.length === 2 && index === 0 ? 0.4 : undefined}
        />
      ))}
    </svg>
  );
}
